import { useState, useEffect, useRef, useMemo } from 'react';
import { db } from './firebase-config';
import { getActiveCompanyId } from './core/company/companyConfig';
import {
  resolveRoutesCollectionPath,
  resolveRouteDocPath
} from './core/company/companyPaths';
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';

import RouteCard from './modules/logistics/components/RouteCard';
import RouteCreator from './modules/logistics/components/RouteCreator';
import RouteDetail from './modules/logistics/components/RouteDetail';
import {
  Icon,
  useIsMobile,
  getLocalDateAsString,
  getRouteStatus,
  calculateDistance,
  PROXIMITY_RADIUS_METERS
} from './modules/logistics/components/LogisticsUtils';

export { Icon };

const DEFAULT_COMPANY_ID = 'default';
const formatArsCompact = (value = 0) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

// --- Componente CalendarView para el dashboard del administrador ---
const CalendarView = ({ markedDates, onSelectDate, showMessage, onClose, isMobile, autoCollapseKey = 'default' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [collapsed, setCollapsed] = useState(true);
  const todayString = getLocalDateAsString(new Date());
  const hasAutoCollapsedRef = useRef(false);

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  useEffect(() => {
    setCollapsed(true);
    hasAutoCollapsedRef.current = false;

    if (isMobile) return undefined;

    const timer = window.setTimeout(() => {
      setCollapsed(false);
      hasAutoCollapsedRef.current = true;
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [isMobile, autoCollapseKey]);

  const handleDayClick = (dayString) => {
    const routesForDay = markedDates.filter((md) => md.date === dayString);

    if (routesForDay.length > 0) {
      onSelectDate(dayString);
    } else {
      showMessage('No hay rutas en esta fecha.', 'info');
    }

    if (isMobile) {
      onClose();
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const totalDays = daysInMonth(month, year);
    const startDay = firstDayOfMonth(month, year);

    const blanks = [];
    for (let i = 0; i < startDay; i++) {
      blanks.push(
        <div key={`blank-${i}`} className="p-2 text-center text-slate-400"></div>
      );
    }

    const days = [];
    for (let day = 1; day <= totalDays; day++) {
      const dayString = getLocalDateAsString(new Date(year, month, day));
      const isToday = dayString === todayString;
      const hasRoute = markedDates.some((md) => md.date === dayString);
      const hasProgrammedRoute = markedDates.some(
        (md) => md.date === dayString && md.status === 'programmed'
      );
      const hasActiveOrCompletedRoute = markedDates.some(
        (md) =>
          md.date === dayString &&
          (md.status === 'in_progress' || md.status === 'completed')
      );
      const hasCancelledRoute = markedDates.some(
        (md) => md.date === dayString && md.status === 'cancelled'
      );

      days.push(
        <button
          key={day}
          onClick={() => handleDayClick(dayString)}
          disabled={!hasRoute}
          className={`relative min-h-[4.35rem] overflow-hidden rounded-[18px] border text-center font-semibold shadow-[0_8px_18px_rgba(15,23,42,0.05)] transition-all duration-300
            ${isToday ? 'border-amber-300 bg-[linear-gradient(180deg,#2563eb_0%,#1d4ed8_62%,#1e40af_100%)] text-white shadow-[0_14px_30px_rgba(37,99,235,0.22)]' : 'text-slate-700'}
            ${!hasRoute ? 'border-white/80 bg-white/76 text-slate-400 cursor-not-allowed shadow-none' : 'text-slate-800 hover:-translate-y-[1px] hover:shadow-[0_12px_24px_rgba(15,23,42,0.08)]'}
            ${hasActiveOrCompletedRoute && !isToday ? 'border-emerald-200/80 bg-[linear-gradient(180deg,#ecfdf5,#d1fae5)] hover:bg-emerald-100' : ''}
            ${hasProgrammedRoute && !isToday && !hasActiveOrCompletedRoute ? 'border-violet-200/80 bg-[linear-gradient(180deg,#f5f3ff,#ede9fe)] hover:bg-violet-100' : ''}
            ${hasCancelledRoute && !isToday && !hasActiveOrCompletedRoute && !hasProgrammedRoute ? 'border-rose-200/80 bg-[linear-gradient(180deg,#fff1f2,#ffe4e6)] hover:bg-rose-100' : ''}`}
        >
          {isToday ? (
            <>
              <span className="pointer-events-none absolute inset-0 rounded-[18px] border border-amber-200/60 animate-pulse" />
              <span
                className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-[moduleShine_5.8s_ease-in-out_infinite]"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 48%, rgba(250,204,21,0.36) 52%, transparent 100%)',
                  transform: 'skewX(-18deg)'
                }}
              />
            </>
          ) : null}
          <div className="flex h-full flex-col items-center justify-center px-1 py-2">
          <span className={`text-base font-black tracking-[-0.03em] ${isToday ? 'text-white' : 'text-inherit'}`}>
            {day}
          </span>
          {hasRoute ? (
            <span className={`mt-1 text-[10px] font-bold uppercase tracking-[0.18em] ${isToday ? 'text-blue-100' : 'text-slate-500'}`}>
              {hasActiveOrCompletedRoute ? 'Ruta' : hasProgrammedRoute ? 'Prog.' : hasCancelledRoute ? 'Canc.' : 'Ruta'}
            </span>
          ) : null}
          </div>
          {hasActiveOrCompletedRoute && (
            <div className="absolute bottom-1 right-1 h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-pulse-slow"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </div>
          )}
          {hasProgrammedRoute && !hasActiveOrCompletedRoute && (
            <div className="absolute bottom-1 right-1 h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75 animate-pulse-slow"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
            </div>
          )}
          {hasCancelledRoute && !hasActiveOrCompletedRoute && !hasProgrammedRoute && (
            <div className="absolute bottom-1 right-1 h-2 w-2">
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </div>
          )}
        </button>
      );
    }

    return [...blanks, ...days];
  };

  return (
    <div
      className={`overflow-hidden rounded-[26px] border border-white/80 bg-[linear-gradient(180deg,#fdfefe_0%,#f2f7ff_100%)] shadow-[0_22px_50px_rgba(15,23,42,0.10)] ${
        isMobile ? 'fixed inset-4 z-50 p-4' : 'flex h-full max-h-full min-h-0 w-full max-w-none flex-col p-0'
      }`}
    >
      {!isMobile ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 overflow-hidden">
          <span
            className="absolute inset-x-[-20%] top-0 h-full animate-[moduleShine_7.4s_ease-in-out_infinite]"
            style={{
              background:
                'linear-gradient(105deg, transparent 0%, rgba(250,204,21,0.18) 30%, rgba(255,255,255,0.22) 50%, rgba(250,204,21,0.12) 70%, transparent 100%)',
              transform: 'skewX(-18deg)'
            }}
          />
        </div>
      ) : null}
      {isMobile && (
        <div className="flex justify-end mb-4">
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-200">
            <Icon path="M6 18L18 6M6 6l12 12" className="w-6 h-6" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className="relative w-full border-b border-amber-100/70 bg-[linear-gradient(180deg,rgba(255,247,213,0.96)_0%,rgba(250,250,255,0.98)_34%,rgba(248,251,255,1)_100%)] px-4 py-4 text-left shadow-[inset_0_-1px_0_rgba(251,191,36,0.18),0_10px_22px_rgba(15,23,42,0.06)] transition hover:bg-[linear-gradient(180deg,rgba(255,248,220,0.98)_0%,rgba(251,251,255,1)_38%,rgba(248,251,255,1)_100%)] md:px-5"
      >
        <div className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" />
        <div className="flex items-center justify-between gap-3">
        <span
          onClick={handlePrevMonth}
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/80 bg-white/90 text-slate-700 shadow-[0_10px_22px_rgba(15,23,42,0.08)] transition hover:-translate-y-[1px] hover:bg-white"
          role="button"
          tabIndex={0}
          onMouseDown={(event) => event.stopPropagation()}
          onClickCapture={(event) => {
            event.stopPropagation();
            handlePrevMonth();
          }}
        >
          <Icon path="M15.75 19.5L8.25 12l7.5-7.5" className="w-5 h-5" />
        </span>

        <div className="flex min-w-0 flex-col items-center justify-center text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-700/80">
          Calendario operativo
        </p>
        <h3 className="text-lg font-black tracking-[-0.03em] text-slate-900 md:text-[1.35rem]">
          {currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
        </h3>
        </div>

        <div className="flex items-center gap-2">
        <span
          onClick={handleNextMonth}
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/80 bg-white/90 text-slate-700 shadow-[0_10px_22px_rgba(15,23,42,0.08)] transition hover:-translate-y-[1px] hover:bg-white"
          role="button"
          tabIndex={0}
          onMouseDown={(event) => event.stopPropagation()}
          onClickCapture={(event) => {
            event.stopPropagation();
            handleNextMonth();
          }}
        >
          <Icon path="M8.25 4.5l7.5 7.5-7.5 7.5" className="w-5 h-5" />
        </span>
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-white/70 bg-white/75 text-slate-500 shadow-[0_10px_18px_rgba(15,23,42,0.05)] transition ${collapsed ? 'rotate-180' : ''}`}>
          <Icon path="M19.5 14.25L12 6.75l-7.5 7.5" className="h-4 w-4" />
        </span>
        </div>
        </div>
      </button>

      <div className={`grid grid-cols-7 border-b border-slate-200/80 bg-white/70 px-3 py-3 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 transition-all duration-500 md:px-4 ${collapsed ? 'shadow-[0_14px_26px_rgba(15,23,42,0.06)]' : ''}`}>
        {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
          <div key={day} className="px-1 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className={`grid min-h-0 flex-1 grid-cols-7 gap-2 overflow-hidden bg-[radial-gradient(circle_at_top,rgba(191,219,254,0.28),transparent_46%)] p-3 transition-all duration-500 md:p-4 ${collapsed ? 'max-h-0 border-t-0 p-0 opacity-0' : 'max-h-[1000px] opacity-100'}`}>
        {renderCalendar()}
      </div>
    </div>
  );
};

// --- Componente Principal de Logística ---
const LogisticsApp = ({
  userId,
  userRole,
  showMessage,
  onBackToMenu,
  addLogisticsNotification,
  selectedTransport,
  onBackToDashboard,
  initialRouteIntent = null,
  onConsumeInitialRouteIntent,
  companyModules = null,
  companyPlanSettings = null,
  commercialState = null,
  onGiaContextChange,
  onStartRouteCreatorTutorial,
  routeCreatorTutorialSeen = false,
  tutorialActive = false,
  tutorialFocusTarget = null
  }) => {
  const [view, setView] = useState('dashboard');
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transporterLocation, setTransporterLocation] = useState(null);
  const [transporterPath, setTransporterPath] = useState([]);
  const [, setShowCalendar] = useState(false);
  const [filterDate, setFilterDate] = useState(null);
  const [highlightedRouteId, setHighlightedRouteId] = useState(null);
  const [calendarAutoCollapseKey, setCalendarAutoCollapseKey] = useState(0);

  const isMobile = useIsMobile();
  const watchIdRef = useRef(null);

  const activeCompanyId = getActiveCompanyId() || DEFAULT_COMPANY_ID;
  const effectiveCompanyId = selectedTransport?.companyId || activeCompanyId;

  const selectedTransportId = selectedTransport?.id || '';
  const selectedTransportSlug = selectedTransport?.slug || '';
  const selectedTransportType = selectedTransport?.transportType || '';
  const selectedTransportName = selectedTransport?.name || '';
  const effectiveModules = selectedTransport?.companyModules || companyModules || null;
  const effectiveCommercialState = selectedTransport?.commercialState || commercialState || null;
  const reportsEnabled = effectiveModules?.reports?.enabled !== false;
  const planLabel = effectiveCommercialState?.planMeta?.label || companyPlanSettings?.planName || 'Plan actual';

  const routesToRender = useMemo(() => {
    const filtered = routes.filter((r) => {
      if (r.transporterId && selectedTransportId) {
        return r.transporterId === selectedTransportId;
      }

      if (r.transporterSlug && selectedTransportSlug) {
        return r.transporterSlug === selectedTransportSlug;
      }

      return r.transport === selectedTransportType || r.transportType === selectedTransportType;
    });

    if (filterDate) {
      const statusPriority = {
        in_progress: 1,
        pending: 2,
        programmed: 2,
        completed: 3,
        cancelled: 4
      };

      return filtered
        .filter((route) => route.date === filterDate)
        .sort((a, b) => {
          const statusA = getRouteStatus(a);
          const statusB = getRouteStatus(b);

          if (statusPriority[statusA] !== statusPriority[statusB]) {
            return statusPriority[statusA] - statusPriority[statusB];
          }

          const dateA = new Date(a.date);
          const dateB = new Date(b.date);

          if (statusA === 'completed') {
            return dateB.getTime() - dateA.getTime();
          }

          return dateA.getTime() - dateB.getTime();
        });
    }

    const today = getLocalDateAsString(new Date());

    const inProgressRouteToday = filtered.find(
      (r) => r.date === today && getRouteStatus(r) === 'in_progress'
    );
    if (inProgressRouteToday) {
      return [inProgressRouteToday];
    }

    const programmedOrPendingToday = filtered.find(
      (r) => r.date === today && (r.status === 'programmed' || r.status === 'pending')
    );
    if (programmedOrPendingToday) {
      return [programmedOrPendingToday];
    }

    const completedRoutes = filtered
      .filter((r) => r.status === 'completed')
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (completedRoutes.length > 0) {
      return [completedRoutes[0]];
    }

    const statusPriority = {
      in_progress: 1,
      pending: 2,
      programmed: 2,
      completed: 3,
      cancelled: 4
    };

    return [...filtered].sort((a, b) => {
          const statusA = getRouteStatus(a);
          const statusB = getRouteStatus(b);

      if (statusPriority[statusA] !== statusPriority[statusB]) {
        return statusPriority[statusA] - statusPriority[statusB];
      }

      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (statusA === 'completed') {
        return dateB.getTime() - dateA.getTime();
      }

      return dateA.getTime() - dateB.getTime();
    });
  }, [routes, filterDate, selectedTransportId, selectedTransportSlug, selectedTransportType]);

  const transporterActiveRoute = useMemo(() => {
    if (userRole !== 'transportista') return null;

    const today = getLocalDateAsString(new Date());
    return routes.find((r) => r.date === today && r.status === 'in_progress');
  }, [routes, userRole]);

  const routeMetricsSummary = useMemo(() => {
    const summary = routesToRender.reduce(
      (acc, route) => {
        const destinations = Array.isArray(route?.destinations) ? route.destinations : [];
        acc.routes += 1;
        acc.completed += destinations.filter((dest) => dest.status === 'completed').length;
        acc.cancelled += destinations.filter((dest) => dest.status === 'cancelled').length;

        const roadKm =
          Number(route?.roadMetrics?.totalDistanceKm || 0) ||
          Number(route?.estimatedDistanceKm || 0) ||
          0;
        acc.km += roadKm;

        const liquidationSnapshot = route?.liquidationSnapshot;
        if (liquidationSnapshot) {
          const pricePerKm = Number(liquidationSnapshot.pricePerKm || 0);
          const baseRouteFee = Number(liquidationSnapshot.baseRouteFee || 0);
          const minimumRouteFee = Number(liquidationSnapshot.minimumRouteFee || 0);
          const routeSettlement = Math.max(minimumRouteFee, baseRouteFee + roadKm * pricePerKm);
          acc.liquidation += routeSettlement;
          acc.hasLiquidation = acc.hasLiquidation || routeSettlement > 0;
        }

        return acc;
      },
      {
        routes: 0,
        completed: 0,
        cancelled: 0,
        km: 0,
        liquidation: 0,
        hasLiquidation: false
      }
    );

    return {
      ...summary,
      kmLabel: `${summary.km.toFixed(1)} km`,
      liquidationLabel: summary.hasLiquidation ? formatArsCompact(summary.liquidation) : 'No disponible'
    };
  }, [routesToRender]);

  useEffect(() => {
    if (!userId || !selectedTransport) {
      setLoading(false);
      return;
    }

    const routesRef = collection(db, resolveRoutesCollectionPath(effectiveCompanyId));

    let routesQuery;
    if (selectedTransport.id) {
      routesQuery = query(routesRef, where('transporterId', '==', selectedTransport.id));
    } else if (selectedTransport.slug) {
      routesQuery = query(routesRef, where('transporterSlug', '==', selectedTransport.slug));
    } else {
      routesQuery = query(routesRef, where('transport', '==', selectedTransport.transportType));
    }

    const unsubscribe = onSnapshot(
      routesQuery,
      (snapshot) => {
        const routesData = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        setRoutes(routesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error al obtener rutas:', error);
        showMessage('Error al cargar las rutas.', 'error');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, showMessage, selectedTransport, effectiveCompanyId]);

  useEffect(() => {
    if (!initialRouteIntent?.routeId) return;

    const targetCompanyId = initialRouteIntent.companyId || effectiveCompanyId;
    if (targetCompanyId !== effectiveCompanyId) return;

    const targetRoute = routes.find((route) => route.id === initialRouteIntent.routeId);
    if (!targetRoute) return;

    const matchesSelectedTransport =
      !selectedTransportId ||
      (targetRoute.transporterId && targetRoute.transporterId === selectedTransportId) ||
      (targetRoute.transporterSlug && selectedTransportSlug && targetRoute.transporterSlug === selectedTransportSlug) ||
      (targetRoute.transportType && selectedTransportType && targetRoute.transportType === selectedTransportType) ||
      (targetRoute.transport && selectedTransportType && targetRoute.transport === selectedTransportType);

    if (!matchesSelectedTransport) return;

    setSelectedRoute(targetRoute);
    setView('routeDetail');

    if (typeof onConsumeInitialRouteIntent === 'function') {
      onConsumeInitialRouteIntent();
    }
  }, [
    initialRouteIntent,
    routes,
    effectiveCompanyId,
    selectedTransportId,
    selectedTransportSlug,
    selectedTransportType,
    onConsumeInitialRouteIntent
  ]);

  useEffect(() => {
    if (!selectedTransport?.initialRouteEntry?.routeId) return;

    const targetRoute = routes.find((route) => route.id === selectedTransport.initialRouteEntry.routeId);
    if (!targetRoute) return;

    setSelectedRoute(targetRoute);
    setView('routeDetail');
    setShowCalendar(false);
    setFilterDate(null);
  }, [selectedTransport, routes]);

  useEffect(() => {
    const initialView = selectedTransport?.initialLogisticsView;
    if (!initialView) return;

    if (initialView === 'create') {
      setSelectedRoute(null);
      setShowCalendar(false);
      setFilterDate(null);
      setView('create');
      return;
    }

    if (initialView === 'history') {
      setSelectedRoute(null);
      setView('dashboard');
      setShowCalendar(true);
    }
  }, [selectedTransport]);

  // --- SEGUIMIENTO GPS EN TIEMPO REAL PARA TRANSPORTISTA ---
  useEffect(() => {
    if (userRole !== 'transportista' || !transporterActiveRoute?.id) {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }

    if (watchIdRef.current) return;

    if (!('geolocation' in navigator)) {
      showMessage('La geolocalización no es compatible o no está permitida.', 'error');
      return;
    }

    const routeCompanyId = transporterActiveRoute.companyId || effectiveCompanyId;
    const routeRef = doc(db, resolveRouteDocPath(routeCompanyId, transporterActiveRoute.id));

    if (transporterActiveRoute.transporterLivePath) {
      setTransporterPath(transporterActiveRoute.transporterLivePath);
    } else {
      setTransporterPath([]);
    }

    const updateLocationInFirestore = async (position) => {
      const { latitude, longitude, accuracy } = position.coords;
      const newLocation = {
        lat: latitude,
        lng: longitude,
        timestamp: new Date(),
        accuracy
      };

      setTransporterLocation(newLocation);

      setTransporterPath((prevPath) => {
        const newPath = [...prevPath, newLocation];

        updateDoc(routeRef, {
          transporterLastLocation: newLocation,
          transporterLivePath: newPath
        }).catch((err) => console.error('Error updating location:', err));

        return newPath;
      });

      const currentRouteState = routes.find((r) => r.id === transporterActiveRoute.id);
      if (!currentRouteState) return;

      const pendingDestinations = currentRouteState.destinations.filter(
        (d) => d.status === 'pending'
      );

      if (pendingDestinations.length > 0) {
        const nextDestination = pendingDestinations.sort((a, b) => a.order - b.order)[0];

        if (nextDestination.latlng) {
          const distanceToNext =
            calculateDistance(
              latitude,
              longitude,
              nextDestination.latlng.lat,
              nextDestination.latlng.lng
            ) * 1000;

          if (
            distanceToNext <= PROXIMITY_RADIUS_METERS &&
            !currentRouteState.destinations.some((d) => d.status === 'en_destino')
          ) {
            const updatedDestinations = currentRouteState.destinations.map((d) =>
              d.id === nextDestination.id ? { ...d, status: 'en_destino' } : d
            );

            await updateDoc(routeRef, { destinations: updatedDestinations });
            showMessage(`Estás cerca de ${nextDestination.name}.`, 'info');
            addLogisticsNotification(`Transportista ha llegado a ${nextDestination.name}.`);
          }
        }
      }
    };

    const handleError = (error) => {
      let msg = 'Error de geolocalización.';
      if (error.code === 1) msg = 'Permiso de geolocalización denegado.';
      showMessage(msg, 'error');
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      updateLocationInFirestore,
      handleError,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [
    userRole,
    transporterActiveRoute,
    showMessage,
    addLogisticsNotification,
    routes,
    effectiveCompanyId
  ]);

  const handleSelectRoute = (route) => {
    setSelectedRoute(route);
    setView('routeDetail');
  };

  const handleCreateSuccess = (newRoute) => {
    setSelectedRoute(newRoute);
    setView('routeDetail');
  };

  const handleBackToDashboard = () => {
    setSelectedRoute(null);
    setView('dashboard');
    setShowCalendar(false);
    setCalendarAutoCollapseKey((prev) => prev + 1);
  };

  const handleBackToHistory = () => {
    setSelectedRoute(null);
    setView('dashboard');
    setShowCalendar(true);
    setCalendarAutoCollapseKey((prev) => prev + 1);
  };

  const handleDeleteRoute = async (e, routeId, routeCompanyId = effectiveCompanyId) => {
    e.stopPropagation();

    if (
      !window.confirm(
        '¿Estás seguro de que quieres eliminar esta hoja de ruta? Esta acción no se puede deshacer.'
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, resolveRouteDocPath(routeCompanyId, routeId)));
      showMessage('Hoja de ruta eliminada con éxito.', 'success');
    } catch (error) {
      console.error('Error al eliminar la hoja de ruta:', error);
      showMessage('No se pudo eliminar la hoja de ruta.', 'error');
    }
  };

  const handleSelectDate = (date) => {
    setFilterDate(date);

    const routeForDate = routes.find((r) => r.date === date);
    if (routeForDate) {
      setHighlightedRouteId(routeForDate.id);
      setTimeout(() => setHighlightedRouteId(null), 3000);
    }
  };

  useEffect(() => {
    if (view === 'dashboard') {
      setCalendarAutoCollapseKey((prev) => prev + 1);
    }
  }, [view, selectedTransportId]);

  useEffect(() => {
    if (typeof onGiaContextChange !== 'function') return;

    let subsectionLabel = 'Base operativa';
    if (view === 'create') {
      subsectionLabel = 'Nueva hoja de ruta';
    } else if (view === 'routeDetail') {
      subsectionLabel = 'Hoja de ruta';
    } else if (filterDate) {
      subsectionLabel = 'Historial por fecha';
    }

    const focusLabel =
      selectedRoute?.routeName ||
      selectedRoute?.name ||
      selectedRoute?.id ||
      selectedTransportName ||
      '';

    onGiaContextChange({
      subsectionLabel,
      focusLabel: view === 'dashboard' ? selectedTransportName || '' : focusLabel,
    });
  }, [filterDate, onGiaContextChange, selectedRoute, selectedTransportName, view]);

  const markedDates = useMemo(() => {
    return routes.map((route) => ({
      date: route.date,
      status: getRouteStatus(route)
    }));
  }, [routes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Icon
          path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
          className="w-12 h-12 text-blue-500 animate-spin"
        />
      </div>
    );
  }

  // ** VISTA PARA TRANSPORTISTA **
  if (userRole === 'transportista') {
    return (
      <div className="p-2 md:p-6 bg-slate-100 min-h-screen flex flex-col">
        <div className="p-4 border-b bg-white flex justify-between items-center">
          <button
            onClick={onBackToMenu}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors font-semibold"
          >
            <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="w-5 h-5" />
            <span>Volver al Menú</span>
          </button>

          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-slate-800">
            Mi Ruta Activa
          </h1>
        </div>

        <div className="flex-grow pt-6">
          {transporterActiveRoute ? (
            <RouteDetail
              route={transporterActiveRoute}
              showMessage={showMessage}
              addLogisticsNotification={addLogisticsNotification}
              onBack={onBackToMenu}
              userRole={userRole}
              transporterLocation={transporterLocation}
              transporterPath={transporterPath}
              companyId={transporterActiveRoute.companyId || effectiveCompanyId}
            />
          ) : (
            <div className="text-center py-16 bg-white rounded-lg shadow-md h-full flex flex-col justify-center items-center p-4">
              <Icon
                path="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128z"
                className="w-16 h-16 text-slate-400 mb-4"
              />
              <h3 className="text-xl font-semibold text-slate-700">
                No tienes una ruta asignada para hoy.
              </h3>
              <p className="text-slate-500 mt-2">
                Por favor, espera a que se te asigne una ruta o contacta a tu administrador.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ** VISTA PARA ADMIN **
  return (
    <div className="p-2 md:p-6 bg-slate-100 min-h-screen flex flex-col">
      {view !== 'routeDetail' && (
        <div className="mb-5 overflow-hidden rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#eef4ff_100%)] p-4 shadow-[0_18px_40px_rgba(15,23,42,0.08)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={onBackToDashboard}
                className="inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#1e293b,#0f172a)] px-4 py-2 text-sm font-bold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-[1px] hover:bg-slate-800"
              >
                <Icon path="M9 15L3 9m0 0l6-6M3 12h12a6 6 0 010 12h-3" className="h-4 w-4" />
                <span>Volver</span>
              </button>
              <span className="text-slate-300">|</span>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">Centro operativo</p>
              <h1 className="text-xl font-bold tracking-[-0.03em] text-slate-900">
                Calendario y rutas
              </h1>
              </div>
            <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/80 bg-white/78 px-4 py-2 text-sm font-bold tracking-[-0.01em] text-slate-700 shadow-sm">
              <span className="inline-flex h-2.5 w-2.5 rounded-full bg-sky-500" />
              {selectedTransportName || selectedTransportType || 'Transporte'}
              {filterDate ? (
                <>
                  <span className="text-slate-300">|</span>
                  <span className="text-[12px] font-extrabold tracking-[0] text-slate-600">
                    {new Date(`${filterDate}T00:00:00`).toLocaleDateString('es-AR')}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow pt-6">
        {view === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(360px,0.95fr)_minmax(0,1.05fr)]">
              <div className="xl:sticky xl:top-6 xl:self-start">
                <div className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#eef4ff_100%)] p-4 shadow-[0_18px_40px_rgba(15,23,42,0.08)] xl:h-[calc(100dvh-16rem)] xl:max-h-[calc(100dvh-16rem)]">
                <CalendarView
                  markedDates={markedDates}
                  onSelectDate={handleSelectDate}
                  showMessage={showMessage}
                  onClose={() => {}}
                  isMobile={false}
                  autoCollapseKey={calendarAutoCollapseKey}
                />
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#eef4ff_100%)] p-4 shadow-[0_18px_40px_rgba(15,23,42,0.08)] xl:min-h-[calc(100dvh-16rem)] xl:max-h-[calc(100dvh-16rem)] xl:overflow-hidden">
                <div className="xl:h-full xl:overflow-y-auto xl:pr-2 xl:[scrollbar-color:#94a3b8_transparent] xl:[scrollbar-width:thin]">
                <div className="sticky top-0 z-10 mb-4 grid grid-cols-2 gap-3 bg-[linear-gradient(180deg,#f8fbff_0%,rgba(248,251,255,0.96)_75%,rgba(248,251,255,0)_100%)] pb-3 pt-1 backdrop-blur-[4px] 2xl:grid-cols-5">
                  <div className="rounded-[18px] border border-white/80 bg-white/78 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Rutas</p>
                    <p className="mt-1 text-[18px] font-black tracking-[-0.03em] text-slate-900">{routeMetricsSummary.routes}</p>
                  </div>
                  <div className="rounded-[18px] border border-white/80 bg-white/78 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Completados</p>
                    <p className="mt-1 text-[18px] font-black tracking-[-0.03em] text-emerald-700">{routeMetricsSummary.completed}</p>
                  </div>
                  <div className="rounded-[18px] border border-white/80 bg-white/78 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Cancelados</p>
                    <p className="mt-1 text-[18px] font-black tracking-[-0.03em] text-rose-700">{routeMetricsSummary.cancelled}</p>
                  </div>
                  <div className="rounded-[18px] border border-white/80 bg-white/78 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Kilómetros</p>
                    <p className={`mt-1 font-black tracking-[-0.03em] ${reportsEnabled ? 'text-[18px] text-slate-900' : 'text-[14px] text-slate-500'}`}>
                      {reportsEnabled ? routeMetricsSummary.kmLabel : planLabel}
                    </p>
                  </div>
                  <div className="col-span-2 rounded-[18px] border border-white/80 bg-white/78 px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.08)] 2xl:col-span-1">
                    <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Liquidación</p>
                    <p className={`mt-1 font-black tracking-[-0.03em] ${reportsEnabled ? 'text-[18px] text-slate-900' : 'text-[14px] text-slate-500'}`}>
                      {reportsEnabled ? routeMetricsSummary.liquidationLabel : planLabel}
                    </p>
                  </div>
                </div>
                {filterDate && (
                  <div className="mb-4 flex items-center rounded-[20px] border border-white/80 bg-white/78 p-4 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                    <h3 className="text-xl font-bold">
                      Rutas para el {new Date(`${filterDate}T00:00:00`).toLocaleDateString('es-AR')}
                    </h3>
                  </div>
                )}

                {routesToRender.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {routesToRender.map((route) => (
                      <RouteCard
                        key={route.id}
                        route={route}
                        onSelect={handleSelectRoute}
                        onDelete={(e, routeId) => handleDeleteRoute(e, routeId, route.companyId || effectiveCompanyId)}
                        userRole={userRole}
                        isHighlighted={highlightedRouteId === route.id}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 rounded-[22px] border border-white/80 bg-white/78 shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                    <h3 className="text-xl font-semibold text-slate-700">
                      No hay rutas para la fecha seleccionada.
                    </h3>
                    <p className="text-slate-500 mt-2">
                      Probá seleccionar otra fecha.
                    </p>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        )}

        {view === 'create' && (
          <RouteCreator
            userId={userId}
            showMessage={showMessage}
            addLogisticsNotification={addLogisticsNotification}
            onBack={handleBackToDashboard}
            onCreateSuccess={handleCreateSuccess}
            selectedTransport={selectedTransport}
            companyId={effectiveCompanyId}
            companyName={selectedTransport?.companyName}
            planLabel={planLabel}
            onStartTutorial={onStartRouteCreatorTutorial}
            tutorialSeen={routeCreatorTutorialSeen}
            tutorialActive={tutorialActive}
            tutorialFocusTarget={tutorialFocusTarget}
          />
        )}

        {view === 'routeDetail' && selectedRoute && (
          <RouteDetail
            route={selectedRoute}
            showMessage={showMessage}
            addLogisticsNotification={addLogisticsNotification}
            onBack={handleBackToDashboard}
            onBackToHistory={handleBackToHistory}
            userRole={userRole}
            transporterLocation={selectedRoute.transporterLastLocation || null}
            transporterPath={selectedRoute.transporterLivePath || []}
            onBackToDashboard={onBackToDashboard}
            companyId={selectedRoute.companyId || effectiveCompanyId}
          />
        )}
      </div>
    </div>
  );
};

export default LogisticsApp;
